/**
 * Cross-Tab Synchronization - Web Implementation
 *
 * Provides cross-tab communication for token refresh coordination
 * Uses BroadcastChannel, navigator.locks, and storage events
 */

import {
  type CrossTabSync,
  type CrossTabConfig,
  type CrossTabMessage,
  type TokenRefreshSuccessPayload,
  type LoginPayload,
  type AuthSyncCallbacks,
  CrossTabMessageType,
  DEFAULT_CROSS_TAB_CONFIG,
} from "./types";

export * from "./types";

function generateTabId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isBroadcastChannelSupported(): boolean {
  return isBrowser() && "BroadcastChannel" in window;
}

function isNavigatorLocksSupported(): boolean {
  return isBrowser() && "locks" in navigator;
}

class WebCrossTabSync implements CrossTabSync {
  private config: Required<CrossTabConfig> = DEFAULT_CROSS_TAB_CONFIG;
  private tabId: string = generateTabId();
  private channel: BroadcastChannel | null = null;
  private messageCallbacks: Set<(message: CrossTabMessage) => void> = new Set();
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private isRefreshing = false;
  private initialized = false;

  initialize(config?: CrossTabConfig): void {
    if (this.initialized) {
      return;
    }
    // Reference to reserved method to prevent unused warning
    void this._checkLockHealth;

    this.config = { ...DEFAULT_CROSS_TAB_CONFIG, ...config };

    if (!this.config.enabled || !isBrowser()) {
      return;
    }

    if (isBroadcastChannelSupported()) {
      try {
        this.channel = new BroadcastChannel(this.config.channelName);
        this.channel.onmessage = (event: MessageEvent<CrossTabMessage>) => {
          this.handleMessage(event.data);
        };
      } catch (error) {
        console.warn("[CrossTab] Failed to create BroadcastChannel:", error);
      }
    }

    this.storageListener = (event: StorageEvent) => {
      if (event.key === this.config.storageKey && event.newValue) {
        try {
          const message = JSON.parse(event.newValue) as CrossTabMessage;
          if (message.tabId !== this.tabId) {
            this.handleMessage(message);
          }
        } catch {
          // Invalid JSON, ignore
        }
      }
    };
    window.addEventListener("storage", this.storageListener);

    this.initialized = true;
  }

  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
      this.storageListener = null;
    }

    this.messageCallbacks.clear();
    this.initialized = false;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      this.initialize();
    }
  }

  broadcast(message: Omit<CrossTabMessage, "timestamp" | "tabId">): void {
    this.ensureInitialized();

    if (!this.config.enabled) {
      if (
        isBrowser() &&
        (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
      ) {
        console.log("[CrossTab] Broadcast skipped - disabled");
      }
      return;
    }

    const fullMessage: CrossTabMessage = {
      ...message,
      timestamp: Date.now(),
      tabId: this.tabId,
    };

    if (
      isBrowser() &&
      (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
    ) {
      console.log("[CrossTab] Broadcasting message", {
        type: message.type,
        hasChannel: !!this.channel,
        tabId: this.tabId,
      });
    }

    if (this.channel) {
      try {
        this.channel.postMessage(fullMessage);
      } catch (error) {
        console.warn("[CrossTab] Failed to post message:", error);
      }
    }

    if (isBrowser()) {
      try {
        localStorage.setItem(
          this.config.storageKey,
          JSON.stringify(fullMessage),
        );
        setTimeout(() => {
          try {
            localStorage.removeItem(this.config.storageKey);
          } catch {
            // Ignore cleanup errors
          }
        }, 100);
      } catch {
        // localStorage not available or quota exceeded
      }
    }
  }

  onMessage(callback: (message: CrossTabMessage) => void): () => void {
    this.ensureInitialized();
    this.messageCallbacks.add(callback);
    return () => {
      this.messageCallbacks.delete(callback);
    };
  }

  async withLock<T>(callback: () => Promise<T>): Promise<T> {
    if (!this.config.enabled) {
      return await callback();
    }

    const startTime = Date.now();

    try {
      if (isNavigatorLocksSupported()) {
        return await this.withNavigatorLock(callback);
      }
      return await this.withLocalStorageLock(callback);
    } catch (error) {
      // Log lock acquisition time for monitoring
      const duration = Date.now() - startTime;
      if (duration > this.config.lockTimeout * 0.8) {
        console.warn(
          `[CrossTab] Lock acquisition took ${duration}ms (${((duration / this.config.lockTimeout) * 100).toFixed(0)}% of timeout)`,
        );
      }
      throw error;
    }
  }

  private async withNavigatorLock<T>(callback: () => Promise<T>): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      this.config.lockTimeout,
    );

    try {
      return await navigator.locks.request(
        this.config.lockName,
        { signal: controller.signal },
        async () => {
          clearTimeout(timeoutId);
          return callback();
        },
      );
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Lock acquisition timeout");
      }
      throw error;
    }
  }

  private async withLocalStorageLock<T>(
    callback: () => Promise<T>,
  ): Promise<T> {
    const lockKey = `${this.config.storageKey}-lock`;
    const lockValue = `${this.tabId}-${Date.now()}`;
    const startTime = Date.now();

    while (Date.now() - startTime < this.config.lockTimeout) {
      const existingLock = localStorage.getItem(lockKey);

      if (!existingLock) {
        localStorage.setItem(lockKey, lockValue);

        // Wait a bit to ensure lock is stable (prevent race conditions)
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Verify we still hold the lock (another tab might have taken it)
        if (localStorage.getItem(lockKey) === lockValue) {
          try {
            return await callback();
          } finally {
            try {
              localStorage.removeItem(lockKey);
            } catch {
              // Ignore cleanup errors
            }
          }
        }
      } else {
        // Check for stale locks and clean them up
        const lockParts = existingLock.split("-");
        const lockTime = parseInt(lockParts[lockParts.length - 1], 10);
        const lockAge = Date.now() - lockTime;

        if (lockAge > this.config.lockTimeout) {
          try {
            localStorage.removeItem(lockKey);
          } catch {
            // Ignore cleanup errors
          }
          continue;
        }
      }

      // Polling interval - adjust based on lock timeout
      const pollInterval = Math.min(100, this.config.lockTimeout / 10);
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    throw new Error("Lock acquisition timeout");
  }

  /**
   * Health check for lock mechanism
   * Useful for monitoring and debugging
   * @internal
   * @deprecated Reserved for future use
   */

  private async _checkLockHealth(): Promise<boolean> {
    const lockKey = `${this.config.storageKey}-lock`;
    const existingLock = localStorage.getItem(lockKey);

    if (!existingLock) {
      return true;
    }

    const lockParts = existingLock.split("-");
    const lockTime = parseInt(lockParts[lockParts.length - 1], 10);
    const age = Date.now() - lockTime;

    // Lock is healthy if it's not stale
    return age < this.config.lockTimeout;
  }

  isRefreshingInOtherTab(): boolean {
    return this.isRefreshing;
  }

  notifyTokenUpdate(tokens: TokenRefreshSuccessPayload): void {
    this.broadcast({
      type: CrossTabMessageType.TOKEN_REFRESH_SUCCESS,
      payload: tokens,
    });
  }

  notifyLogin(payload: LoginPayload): void {
    if (
      isBrowser() &&
      (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
    ) {
      console.log("[CrossTab] Broadcasting LOGIN", payload);
    }
    this.broadcast({
      type: CrossTabMessageType.LOGIN,
      payload,
    });
  }

  notifyLogout(): void {
    if (
      isBrowser() &&
      (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
    ) {
      console.log("[CrossTab] Broadcasting LOGOUT");
    }
    this.broadcast({
      type: CrossTabMessageType.LOGOUT,
    });
  }

  subscribeToAuthEvents(callbacks: AuthSyncCallbacks): () => void {
    if (
      isBrowser() &&
      (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
    ) {
      console.log("[CrossTab] Subscribing to auth events", {
        initialized: this.initialized,
        hasChannel: !!this.channel,
      });
    }

    const handler = (message: CrossTabMessage) => {
      if (
        isBrowser() &&
        (window as unknown as Record<string, unknown>).__CROSS_TAB_DEBUG__
      ) {
        console.log("[CrossTab] Received message", message);
      }

      switch (message.type) {
        case CrossTabMessageType.LOGIN:
          if (callbacks.onLogin) {
            callbacks.onLogin(message.payload as LoginPayload);
          }
          break;
        case CrossTabMessageType.LOGOUT:
          if (callbacks.onLogout) {
            callbacks.onLogout();
          }
          break;
        case CrossTabMessageType.TOKEN_REFRESH_SUCCESS:
        case CrossTabMessageType.TOKEN_UPDATED:
          if (callbacks.onTokenUpdate) {
            callbacks.onTokenUpdate(
              message.payload as TokenRefreshSuccessPayload,
            );
          }
          break;
      }
    };

    return this.onMessage(handler);
  }

  isSupported(): boolean {
    return isBrowser() && (isBroadcastChannelSupported() || true);
  }

  private handleMessage(message: CrossTabMessage): void {
    if (message.tabId === this.tabId) {
      return;
    }

    if (message.type === CrossTabMessageType.TOKEN_REFRESH_START) {
      this.isRefreshing = true;
    } else if (
      message.type === CrossTabMessageType.TOKEN_REFRESH_SUCCESS ||
      message.type === CrossTabMessageType.TOKEN_REFRESH_FAILURE
    ) {
      this.isRefreshing = false;
    }

    this.messageCallbacks.forEach((callback) => {
      try {
        callback(message);
      } catch (error) {
        console.error("[CrossTab] Error in message callback:", error);
      }
    });
  }
}

let instance: CrossTabSync | null = null;

export function getCrossTabSync(): CrossTabSync {
  if (!instance) {
    instance = new WebCrossTabSync();
  }
  return instance;
}

export function resetCrossTabSync(): void {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}
